import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, Coins } from '../../lib/icons';
import { fmt } from '../shared/badges';
import { fetchDroitsHuile, convertirHuileEnDette, FAMILLES } from '../../lib/risqueHuile';
import { getCoursDuJour } from '../../lib/cours';
import { useAuth } from '../../lib/auth';

const champ =
  'mt-1 h-10 w-full border border-slate-300 bg-slate-50 px-2 font-data text-sm text-slate-900 outline-none focus:border-slate-900 focus:bg-white';
const label = 'text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';

// Conversion d'un droit en nature (kg d'huile) en dette monétaire (TND).
// Le stock physique et la trésorerie restent inchangés.
export const ConversionDialog = ({ cleInitiale = '', onClose, onDone }) => {
  const { user } = useAuth();
  const [droits, setDroits] = useState([]);
  const [cle, setCle] = useState(cleInitiale);
  const [f, setF] = useState({
    kg: '',
    prixKg: '',
    dateEffet: new Date().toISOString().slice(0, 10),
    echeance: '',
    reference: '',
    notes: '',
    effetAuPaiement: false,
  });

  useEffect(() => {
    const rows = fetchDroitsHuile().filter((d) => d.droitKg > 0.001);
    setDroits(rows);
    setCle((c) => c || rows[0]?.cle || '');
  }, []);

  const fiche = droits.find((d) => d.cle === cle) ?? null;
  useEffect(() => {
    if (!fiche) return;
    const cours = getCoursDuJour();
    setF((x) => ({ ...x, prixKg: x.prixKg || String(cours[fiche.famille] ?? '') }));
  }, [fiche]);

  const kg = Number(f.kg) || 0;
  const montant = Number((kg * (Number(f.prixKg) || 0)).toFixed(3));

  const valider = async () => {
    if (!fiche) return toast.error('Sélectionnez un compte huile client.');
    const { error, row } = await convertirHuileEnDette({
      tiersId: fiche.tiersId,
      client: fiche.nom,
      famille: fiche.famille,
      kg,
      prixKg: f.prixKg,
      dateEffet: f.dateEffet,
      echeance: f.echeance,
      reference: f.reference,
      notes: f.notes,
      effetAuPaiement: f.effetAuPaiement,
      utilisateur: user?.name,
    });
    if (error) return toast.error(error, { duration: 8000 });
    toast.success(
      `${row.id} — ${fmt(row.kg, 3)} kg convertis à ${fmt(row.prixKg, 3)} TND/kg : ${fmt(row.montant, 3)} TND à payer à ${row.client}${
        row.effetAuPaiement ? ' (droits conservés jusqu’au règlement)' : ''
      }`,
      { duration: 10000 }
    );
    onDone?.();
    onClose();
  };

  return (
    <div data-testid="modale-conversion" className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-slate-900/60 p-4">
      <div className="w-full max-w-2xl border border-slate-200 bg-white">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-black text-slate-900">
              <Coins size={18} className="text-amber-700" /> Conversion huile → dette monétaire
            </h2>
            <p className="text-xs text-slate-500">
              Le droit en kg du client diminue et une dette en TND est créée dans son compte tiers.
              Le stock physique et la trésorerie restent inchangés jusqu'au paiement.
            </p>
          </div>
          <button type="button" data-testid="conversion-fermer" onClick={onClose} className="border border-slate-300 p-2 text-slate-600 hover:bg-slate-900 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 px-5 py-4 sm:grid-cols-2">
          <label className="sm:col-span-2">
            <span className={label}>Client & qualité concernée</span>
            <select data-testid="conversion-compte" value={cle} onChange={(e) => setCle(e.target.value)} className={champ}>
              <option value="">— Sélectionner —</option>
              {droits.map((d) => (
                <option key={d.cle} value={d.cle}>
                  {`${d.nom} — ${FAMILLES.find((x) => x.id === d.famille)?.label ?? d.famille} — droit ${d.droitKg.toFixed(3)} kg`}
                </option>
              ))}
            </select>
          </label>

          <div className="border border-slate-200 bg-slate-50 px-3 py-2 sm:col-span-2">
            <p className="font-data text-xs text-slate-700">
              Droit restant :{' '}
              <span data-testid="conversion-droit" className="tabular font-black text-slate-900">
                {fiche ? fmt(fiche.droitKg, 3) : '0,000'} kg
              </span>
              {fiche?.converti > 0 && <span className="text-slate-500"> · déjà converti {fmt(fiche.converti, 3)} kg</span>}
            </p>
          </div>

          <label>
            <span className={label}>Quantité à convertir (kg)</span>
            <input data-testid="conversion-kg" type="number" step="0.001" value={f.kg} onChange={(e) => setF({ ...f, kg: e.target.value })} className={champ} />
          </label>
          <label>
            <span className={label}>Prix négocié (TND/kg)</span>
            <input data-testid="conversion-prix" type="number" step="0.001" value={f.prixKg} onChange={(e) => setF({ ...f, prixKg: e.target.value })} className={champ} />
          </label>
          <label>
            <span className={label}>Date d'effet</span>
            <input data-testid="conversion-date" type="date" value={f.dateEffet} onChange={(e) => setF({ ...f, dateEffet: e.target.value })} className={champ} />
          </label>
          <label>
            <span className={label}>Échéance de paiement</span>
            <input data-testid="conversion-echeance" type="date" value={f.echeance} onChange={(e) => setF({ ...f, echeance: e.target.value })} className={champ} />
          </label>
          <label>
            <span className={label}>Référence / pièce de l'accord</span>
            <input data-testid="conversion-reference" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="ex. ACC-2026-014" className={champ} />
          </label>
          <label>
            <span className={label}>Notes</span>
            <input data-testid="conversion-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={champ} />
          </label>

          <label className="flex items-center gap-2 sm:col-span-2">
            <input data-testid="conversion-effet-paiement" type="checkbox" checked={f.effetAuPaiement} onChange={(e) => setF({ ...f, effetAuPaiement: e.target.checked })} className="h-4 w-4" />
            <span className="text-xs text-slate-700">
              L'accord ne prend effet qu'au paiement — les droits en kg sont conservés jusqu'au règlement
            </span>
          </label>

          <p data-testid="conversion-montant" className="border-l-4 border-amber-500 bg-amber-50 px-3 py-2 font-data text-sm text-amber-900 sm:col-span-2">
            Montant de la dette créée : <span className="tabular font-black">{fmt(montant, 3)} TND</span>
            {fiche && kg > fiche.droitKg + 0.001 && (
              <span className="block font-bold text-red-700">Quantité supérieure au droit disponible — conversion refusée.</span>
            )}
          </p>

          <div className="flex flex-wrap gap-2 sm:col-span-2">
            <button type="button" data-testid="conversion-annuler" onClick={onClose} className="h-11 flex-1 border border-slate-300 text-sm font-bold text-slate-700 hover:bg-slate-100">
              Annuler
            </button>
            <button type="button" data-testid="conversion-valider" onClick={valider} className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold text-white hover:bg-slate-700">
              Valider la conversion
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
